import { useState } from 'react';
import currency from 'currency.js';
import { Tooltip } from 'antd';
import { Close, ErrorWarning } from '@styled-icons/remix-line';

import theme from 'styles/theme';
import Slider from '../Slider';
import Range from '../Range';
import Button from '../Button';
import Carousel from '../Carousel';
import ContentBox from '../ContentBox';
import Collapse from '../Collapse';

import friendsSvg from 'v3/assets/svg/friends.svg';
import extraPaymentsSvg from 'v3/assets/svg/make-extra-payments.svg';
import paymentsOnTimeSvg from 'v3/assets/svg/payments-on-time.svg';
import { DownPaymentResult } from 'v3/api/client/DealsClient';

import * as S from './styles';

export type IncreaseChancesModalProps = {
  open: boolean;
  tooltip: string | undefined;
  vehiclePrice: number;
  terms: 24 | 36 | 48 | 60 | 72 | 84;
  downPaymentData: DownPaymentResult;
  onCancel: () => void;
  onApplyDownPayment: (downPayment: number) => void;
};

const IncreaseChancesModal = ({
  open,
  tooltip,
  terms,
  vehiclePrice,
  downPaymentData,
  onCancel,
  onApplyDownPayment,
}: IncreaseChancesModalProps) => {
  const [downPayment, setDownPayment] = useState(1000);
  const [initialMonth, finalMonth] =
    downPaymentData[downPayment]?.userChanceRange;

  const onSliderChange = (sliderValue: number) => {
    if (sliderValue > vehiclePrice) {
      setDownPayment(vehiclePrice);
      return;
    }

    setDownPayment(sliderValue);
  };

  return (
    <S.Modal
      centered
      footer={null}
      onCancel={onCancel}
      open={open}
      width={730}
      closeIcon={
        <S.CloseButton>
          <Close size={18} color={theme.colors.neutralSilver50} />
        </S.CloseButton>
      }
    >
      <S.ChancesSection>
        <S.Title>Improve Your Chances</S.Title>
        <S.Text size="large" color="neutralSilver60" fontWeight={400}>
          Make a down payment and improve your chance to get a car early.
        </S.Text>
        <S.SpacedItemsSection marginTop="32px">
          <S.Text size="medium" color="neutralSilver60" fontWeight={500}>
            Down Payment
          </S.Text>
          <S.Text size="medium" color="neutralSilver90" fontWeight={500}>
            {currency(downPayment).format()}
          </S.Text>
        </S.SpacedItemsSection>
        <Slider
          min={0}
          max={vehiclePrice}
          value={downPayment}
          step={500}
          onChange={onSliderChange}
        />
        <S.SpacedItemsSection marginTop="30px">
          <S.TooltipContainer>
            <S.Text size="small" color="neutralSilver60" fontWeight={500}>
              80% chance to get a car between months:
            </S.Text>

            {tooltip ?
              <Tooltip title={tooltip}>
                <S.IconWrapper>
                  <ErrorWarning size={24} color={theme.colors.neutralSilver60} />
                </S.IconWrapper>
              </Tooltip>
              : null}

          </S.TooltipContainer>
          <Range
            initialValue={initialMonth}
            finalValue={finalMonth}
            months={terms}
          />
        </S.SpacedItemsSection>
        <S.SpacedItemsSection marginTop="28px">
          <S.Text size="medium" color="neutralSilver60" fontWeight={500}>
            New Monthly Payment
          </S.Text>
          <S.Text size="medium" color="neutralSilver90" fontWeight={500}>
            {currency(downPaymentData[downPayment]?.newMonthlyPayment).format()}
          </S.Text>
        </S.SpacedItemsSection>
        <S.CenteredSection>
          <Button onClick={() => onApplyDownPayment(downPayment)}>
            Apply down payment
          </Button>
        </S.CenteredSection>
      </S.ChancesSection>
      <S.DesktopOnly>
        <S.ExploreMoreSection>
          <S.Text as="h3" size="large" color="neutralSilver70" fontWeight={600}>
            Explore more ways to get a car faster
          </S.Text>
          <S.Text
            size="medium"
            color="neutralSilver60"
            fontWeight={400}
            marginTop="8px"
          >
            Joining savings.club gives you more ways to speed up the process.
          </S.Text>
          <S.CarouselContainer>
            <Carousel>
              <ContentBox
                icon={<S.ContentBoxImage src={friendsSvg} />}
                title="Invite friends"
                text={
                  <p>
                    Invite friends to savings.club and{' '}
                    <strong>
                      earn $100 towards your <br /> new car.
                    </strong>
                  </p>
                }
              />
              <ContentBox
                icon={<S.ContentBoxImage src={extraPaymentsSvg} />}
                title="Make extra payments"
                text={
                  <p>
                    Extra spending money left at the end of the month? Make{' '}
                    <br />
                    additional payments.
                  </p>
                }
              />
              <ContentBox
                icon={<S.ContentBoxImage src={paymentsOnTimeSvg} />}
                title="Pay on time"
                text={
                  <p>
                    Keep all your payments up to date and grow your score to
                    speed up the month range
                  </p>
                }
              />
            </Carousel>
          </S.CarouselContainer>
        </S.ExploreMoreSection>
      </S.DesktopOnly>
      <S.MobileOnly>
        <S.CollapseContainer>
          <Collapse title="More ways to accelarate">
            <S.ExploreMoreSection>
              <S.CarouselContainer>
                <Carousel>
                  <ContentBox
                    icon={<S.ContentBoxImage src={friendsSvg} />}
                    title="Invite friends"
                    text={
                      <p>
                        Invite friends to savings.club and{' '}
                        <strong>earn $100 towards your new car.</strong>
                      </p>
                    }
                  />
                  <ContentBox
                    icon={<S.ContentBoxImage src={extraPaymentsSvg} />}
                    title="Make extra payments"
                    text={
                      <p>
                        Extra spending money left at the end of the month? Make{' '}
                        additional payments.
                      </p>
                    }
                  />
                  <ContentBox
                    icon={<S.ContentBoxImage src={paymentsOnTimeSvg} />}
                    title="Pay on time"
                    text={
                      <p>
                        Keep all your payments up to date and grow your score to
                        speed up the month range
                      </p>
                    }
                  />
                </Carousel>
              </S.CarouselContainer>
            </S.ExploreMoreSection>
          </Collapse>
        </S.CollapseContainer>
      </S.MobileOnly>
    </S.Modal>
  );
};

export default IncreaseChancesModal;
